import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, BellRing, CheckCheck, Clock, Loader2 } from "lucide-react";
import { http, formatApiError } from "../lib/api";

const dt = (s) => (s ? new Date(s).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

const Bar = ({ read, total }) => (
  <div className="flex items-center gap-2">
    <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${total ? (read / total) * 100 : 0}%` }} /></div>
    <span className="text-xs font-bold text-slate-700 whitespace-nowrap">{read}/{total} lu{read > 1 ? "s" : ""}</span>
  </div>
);

const Detail = ({ id, onBack }) => {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => http.get(`/messages/broadcasts/${id}`).then((r) => setB(r.data)).catch((e) => toast.error(formatApiError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const remind = async () => {
    setBusy(true);
    try { const { data } = await http.post(`/messages/broadcasts/${id}/remind`); toast.success(`Relance envoyée à ${data.sent} agent${data.sent > 1 ? "s" : ""}`); await load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (!b) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  const unread = b.total - b.read;
  return (
    <div className="space-y-3" data-testid="broadcast-detail">
      <button type="button" onClick={onBack} data-testid="broadcast-detail-back" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900"><ArrowLeft className="h-3.5 w-3.5" />Historique</button>
      <div className="text-xs text-slate-500">{dt(b.created_at)} · {b.channel_label} · {b.user_name}{b.context ? ` · ${b.context}` : ""}</div>
      <p className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3 whitespace-pre-wrap">{b.content}</p>
      <Bar read={b.read} total={b.total} />
      <div className="border border-slate-200 rounded-md max-h-56 overflow-y-auto divide-y divide-slate-100" data-testid="broadcast-detail-recipients">
        {b.recipients.map((r) => (
          <div key={r.agent_id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`broadcast-recipient-${r.agent_id}`}>
            <span>{r.agent_name}</span>
            {r.read_at ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCheck className="h-3.5 w-3.5" />Lu le {dt(r.read_at)}</span>
              : <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><Clock className="h-3.5 w-3.5" />Non lu</span>}
          </div>
        ))}
      </div>
      {b.last_reminder_at && <p className="text-xs text-slate-500" data-testid="broadcast-last-reminder">Dernière relance : {dt(b.last_reminder_at)} ({b.reminders} relance{b.reminders > 1 ? "s" : ""})</p>}
      <button type="button" onClick={remind} disabled={busy || unread === 0} data-testid="broadcast-remind"
        className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-semibold rounded-md py-2.5 text-sm">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
        {unread > 1 ? `Relancer les ${unread} non-lecteurs` : unread === 1 ? "Relancer le non-lecteur" : "Tout le monde a lu"}
      </button>
    </div>
  );
};

export const BroadcastHistory = () => {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { if (!open) http.get("/messages/broadcasts").then((r) => setList(r.data)).catch((e) => { setList([]); toast.error(formatApiError(e)); }); }, [open]);
  if (open) return <Detail id={open} onBack={() => setOpen(null)} />;
  if (!list) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  if (!list.length) return <p className="text-sm text-slate-500" data-testid="broadcast-history-empty">Aucun message groupé envoyé pour le moment.</p>;
  return (
    <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="broadcast-history">
      {list.map((b) => (
        <button key={b.id} type="button" onClick={() => setOpen(b.id)} data-testid={`broadcast-history-item-${b.id}`}
          className="w-full text-left border border-slate-200 rounded-md p-3 hover:border-sky-400 hover:bg-sky-50/40 transition-colors space-y-1.5">
          <div className="flex items-center gap-2 text-xs text-slate-500"><span className="font-bold text-slate-700">{b.channel_label}</span>{dt(b.created_at)}{b.last_reminder_at && <span className="ml-auto text-amber-700">Relancé le {dt(b.last_reminder_at)}</span>}</div>
          <p className="text-sm line-clamp-2">{b.content}</p>
          {b.context && <p className="text-[11px] text-slate-500">{b.context}</p>}
          <Bar read={b.read} total={b.total} />
        </button>
      ))}
    </div>
  );
};
